import { useState, useMemo } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportBarChart } from '../components/report/ReportBarChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { SortableTable } from '../components/SortableTable';
import { ContextualHelp } from '../components/ContextualHelp';
import { fmtEur, fmtEur2, fmtPct } from '../components/report/formatters';
import { MARGIN_COLORS } from '../lib/chart-theme';
import type { ReportParams } from '../types/widget';

interface TableauRow {
    client_code: string;
    client_label: string;
    montant_achat: number;
    montant_vente: number;
    marge_brute: number;
    part_pct: number;
}

interface HistoRow {
    mois: string;
    client_code?: string;
    client_label?: string;
    categorie?: string;
    marge_brute_mois: number;
    marge_brute_ytd: number;
}

interface DrillContext {
    mois: string;
    source: 'global' | 'client' | 'categorie';
    key: string;
}

const DRILL_COLUMNS = [
    { key: 'date_ecriture', label: 'Date' },
    { key: 'libelle', label: 'Libellé', filterable: true, format: (v: unknown) => String(v ?? '').replace(' (label généré)', '') },
    { key: 'compte', label: 'Compte' },
    { key: 'pennylane_customer_name', label: 'Client (tiers)', filterable: true, format: (v: unknown) => v ? String(v) : '—' },
    { key: 'client_code', label: 'Client (catégorie)', filterable: true },
    { key: 'site_code', label: 'Site' },
    { key: 'fournisseur', label: 'Fournisseur', filterable: true },
    { key: 'service_type', label: 'Type' },
    { key: 'regime', label: 'Catégorie' },
    { key: 'montant', label: 'Montant', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant', formatTotal: (v: number) => fmtEur2(v) },
    {
        key: 'sens',
        label: 'Sens',
        format: (v: unknown, row: Record<string, unknown>) => {
            const label = String(v ?? '');
            const type = String(row.transaction_type ?? '');
            const amount = Number(row.montant ?? 0);
            // Acte normal : ACHAT+ (charge) / VENTE- (produit).
            // Avoir : signe inversé sur la même ligne business (ACHAT- / VENTE+).
            const isAvoir = (type === 'ACHAT' && amount < 0) || (type === 'VENTE' && amount > 0);
            return isAvoir ? `${label} (avoir)` : label;
        },
    },
];

const DASH = '—';

function buildTableColumns(maskMargeColumns: boolean) {
    return [
        { key: 'client_code', label: 'Code', format: (v: unknown) => v ? String(v) : DASH },
        { key: 'client_label', label: 'Client', format: (v: unknown, row: Record<string, unknown>) => row.client_code ? String(v ?? '') : 'Non renseigné' },
        {
            key: 'montant_achat', label: 'Achats', align: 'right' as const,
            format: maskMargeColumns ? () => DASH : (v: unknown) => fmtEur(Number(v)),
            totalKey: 'montant_achat',
            formatTotal: maskMargeColumns ? () => DASH : (v: number) => fmtEur2(v),
        },
        {
            key: 'montant_vente', label: 'Ventes', align: 'right' as const,
            format: (v: unknown) => fmtEur(Number(v)),
            totalKey: 'montant_vente',
            formatTotal: (v: number) => fmtEur2(v),
        },
        {
            key: 'marge_brute', label: 'Marge brute', align: 'right' as const,
            format: maskMargeColumns ? () => DASH : (v: unknown) => fmtEur(Number(v)),
            totalKey: 'marge_brute',
            formatTotal: maskMargeColumns ? () => DASH : (v: number) => fmtEur2(v),
        },
        {
            key: 'part_pct', label: 'Part (%)', align: 'right' as const,
            format: maskMargeColumns ? () => DASH : (v: unknown) => fmtPct(Number(v)),
        },
    ];
}

export function MargeBrute({ filters }: { filters: ReportParams }) {
    const [drill, setDrill] = useState<DrillContext | null>(null);
    const [selectedClients, setSelectedClients] = useState<string[]>([]);

    // Le filtre « Client (tiers) » ne s'applique qu'aux lignes de VENTE
    // (les achats fournisseurs n'ont pas de pennylane_customer_id) :
    // la marge brute, le taux et les achats deviennent incohérents.
    const tiersFilterActive = (filters.pennylane_customer_ids?.length ?? 0) > 0
        || filters.pennylane_customer_id != null;

    const tableau = useReportData<TableauRow>('/api/emis/marge-brute/tableau', filters);
    const parClient = useReportData<HistoRow>('/api/emis/marge-brute/par-client', filters);
    const parCategorie = useReportData<HistoRow>('/api/emis/marge-brute/par-categorie', filters);

    const tableColumns = useMemo(() => buildTableColumns(tiersFilterActive), [tiersFilterActive]);

    // KPIs from tableau
    const kpis = useMemo(() => {
        const rows = tableau.data;
        if (!rows.length) return null;
        const totalVente = rows.reduce((s, r) => s + Number(r.montant_vente), 0);
        const totalAchat = rows.reduce((s, r) => s + Number(r.montant_achat), 0);
        const totalMarge = rows.reduce((s, r) => s + Number(r.marge_brute), 0);
        const tauxMarge = totalVente !== 0 ? (totalMarge / totalVente) * 100 : 0;
        return { totalVente, totalAchat, totalMarge, tauxMarge, nbClients: rows.length };
    }, [tableau.data]);

    // Client list for selector
    const clientOptions = useMemo(() => {
        const codes = [...new Set(parClient.data.map((r) => r.client_code!))];
        return codes.map((code) => ({
            code,
            label: parClient.data.find((r) => r.client_code === code)?.client_label ?? code,
        }));
    }, [parClient.data]);

    // Global aggregated chart (always shown)
    const globalChart = useMemo(() => {
        const months = [...new Set(parClient.data.map((r) => r.mois))].sort();
        const byMonth = new Map<string, { mois: number; ytd: number }>();
        for (const r of parClient.data) {
            const val = Number(r.marge_brute_mois);
            const prev = byMonth.get(r.mois);
            if (prev) prev.mois += val;
            else byMonth.set(r.mois, { mois: val, ytd: 0 });
        }
        let cumul = 0;
        for (const m of months) {
            const entry = byMonth.get(m)!;
            cumul += entry.mois;
            entry.ytd = cumul;
        }
        return {
            months,
            series: [
                { name: 'Marge brute (mois)', data: months.map((m) => byMonth.get(m)?.mois ?? 0), color: MARGIN_COLORS.monthly },
                { name: 'Marge brute (cumulée)', data: months.map((m) => byMonth.get(m)?.ytd ?? 0), color: MARGIN_COLORS.cumulative },
            ],
        };
    }, [parClient.data]);

    // Per-client charts for selected clients
    const clientCharts = useMemo(() => {
        if (selectedClients.length === 0) return [];
        const months = [...new Set(parClient.data.map((r) => r.mois))].sort();
        return selectedClients.map((code) => {
            const rows = parClient.data.filter((r) => r.client_code === code);
            const byMonth = new Map(rows.map((r) => [r.mois, r]));
            const label = rows[0]?.client_label ?? code;
            return {
                code,
                label: code ? `${code} — ${label}` : 'Non renseigné',
                months,
                series: [
                    { name: 'Marge brute (mois)', data: months.map((m) => Number(byMonth.get(m)?.marge_brute_mois ?? 0)), color: MARGIN_COLORS.monthly },
                    { name: 'Marge brute (cumulée)', data: months.map((m) => Number(byMonth.get(m)?.marge_brute_ytd ?? 0)), color: MARGIN_COLORS.cumulative },
                ],
            };
        });
    }, [parClient.data, selectedClients]);

    // Catégorie chart — always aggregated
    const categorieChart = useMemo(() => {
        const months = [...new Set(parCategorie.data.map((r) => r.mois))].sort();
        const categories = [...new Set(parCategorie.data.map((r) => r.categorie!))].sort();
        const byKey = new Map(parCategorie.data.map((r) => [`${r.categorie}-${r.mois}`, r]));

        return {
            months,
            series: categories.map((cat) => ({
                name: cat,
                data: months.map((m) => Number(byKey.get(`${cat}-${m}`)?.marge_brute_mois ?? 0)),
                stack: 'categories',
            })),
        };
    }, [parCategorie.data]);

    const drillParams = useMemo(() => (drill ? { ...filters, mois: drill.mois } : filters), [filters, drill]);

    const toggleClient = (code: string) => {
        setDrill(null);
        setSelectedClients((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
        );
    };

    return (
        <div className="space-y-6">
            {/* Bandeau d'alerte — filtre tiers actif → marge brute incomplète */}
            {tiersFilterActive && (
                <ContextualHelp
                    variant="warning"
                    ariaLabel="Pourquoi la marge brute est-elle masquée"
                    summary={
                        <p>
                            <strong>Attention :</strong> la marge brute affichée ne tient pas compte
                            du filtre « Client (tiers) ». Certaines données sont masquées. Pour des
                            résultats corrects, utilisez le filtre « Client (catégorie) ».
                        </p>
                    }
                    detailsTitle="Pourquoi ce masquage ?"
                    details={
                        <p>
                            Le tiers Pennylane n'est posé que sur les lignes de vente. Filtrer sur
                            ce critère exclut mécaniquement les achats fournisseurs : la marge
                            brute devient égale au CA et le taux à 100 %. Les indicateurs concernés
                            sont remplacés par un tiret pour éviter toute lecture trompeuse.
                        </p>
                    }
                />
            )}

            {/* KPI Strip */}
            {kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KpiCard label="Marge brute totale" value={tiersFilterActive ? DASH : fmtEur(kpis.totalMarge)} loading={tableau.loading} />
                    <KpiCard label="Taux de marge" value={tiersFilterActive ? DASH : fmtPct(kpis.tauxMarge)} loading={tableau.loading} />
                    <KpiCard label="CA ventes" value={fmtEur(kpis.totalVente)} loading={tableau.loading} />
                    <KpiCard label="Clients" value={kpis.nbClients} loading={tableau.loading} />
                </div>
            )}
            {tableau.loading && !kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1,2,3,4].map((i) => <KpiCard key={i} label="" value="" loading />)}
                </div>
            )}

            {/* Tableau marge brute par client — SortableTable */}
            <ReportSection title="Marge brute par client" loading={tableau.loading} error={tableau.error}>
                <SortableTable
                    columns={tableColumns}
                    data={tableau.data as unknown as Record<string, unknown>[]}
                    totals={kpis ? {
                        montant_achat: kpis.totalAchat,
                        montant_vente: kpis.totalVente,
                        marge_brute: kpis.totalMarge,
                    } : undefined}
                />
            </ReportSection>

            {/* Histogramme mensuel — global + comparaison multi-clients */}
            <ReportSection title="Marge brute mensuelle" loading={parClient.loading} error={parClient.error}>
                {tiersFilterActive ? (
                    <UnavailablePlaceholder />
                ) : (
                <>
                {/* Multi-select clients */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-sm text-gray-600">Comparer :</span>
                    {clientOptions.map((c) => {
                        const active = selectedClients.includes(c.code);
                        return (
                            <button
                                key={c.code}
                                onClick={() => toggleClient(c.code)}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                    active
                                        ? 'bg-brand-600 text-white border-brand-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                }`}
                            >
                                {c.code || 'N/R'}
                            </button>
                        );
                    })}
                    {selectedClients.length > 0 && (
                        <button
                            onClick={() => { setSelectedClients([]); setDrill(null); }}
                            className="text-xs text-gray-400 hover:text-gray-600 ml-1"
                        >
                            Tout effacer
                        </button>
                    )}
                </div>

                {/* Global chart — always visible */}
                <ReportBarChart
                    title="Tous clients"
                    categories={globalChart.months}
                    series={globalChart.series}
                    height={350}
                    valueFormatter={fmtEur}
                    onBarClick={(_, mois) => setDrill({ mois, source: 'global', key: 'all' })}
                />
                {drill?.source === 'global' && (
                    <ReportDrillDown
                        title={`Détail des écritures — Tous clients — ${drill.mois}`}
                        endpoint="/api/emis/marge-brute/drill-down"
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrill(null)}
                        columns={DRILL_COLUMNS}
                    />
                )}

                {/* Selected clients grid — 2 cols on lg */}
                {clientCharts.length > 0 && (
                    <div className={`grid gap-6 mt-6 ${clientCharts.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                        {clientCharts.map((chart) => (
                            <div key={chart.code} className="border border-gray-200 rounded-lg p-4">
                                <ReportBarChart
                                    title={chart.label}
                                    categories={chart.months}
                                    series={chart.series}
                                    height={280}
                                    valueFormatter={fmtEur}
                                    onBarClick={(_, mois) => setDrill({ mois, source: 'client', key: chart.code })}
                                />
                                {drill?.source === 'client' && drill.key === chart.code && (
                                    <ReportDrillDown
                                        title={`Détail — ${chart.label} — ${drill.mois}`}
                                        endpoint="/api/emis/marge-brute/drill-down"
                                        params={drillParams}
                                        enabled={true}
                                        onClose={() => setDrill(null)}
                                        columns={DRILL_COLUMNS}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
                </>
                )}
            </ReportSection>

            {/* Histogramme par catégorie — barres empilées */}
            <ReportSection title="Marge brute par catégorie" loading={parCategorie.loading} error={parCategorie.error}>
                {tiersFilterActive ? (
                    <UnavailablePlaceholder />
                ) : (
                    <>
                        <ReportBarChart
                            title="Marge brute mensuelle par catégorie (empilé)"
                            categories={categorieChart.months}
                            series={categorieChart.series}
                            height={350}
                            valueFormatter={fmtEur}
                            onBarClick={(_, mois) => setDrill({ mois, source: 'categorie', key: 'all' })}
                        />
                        {drill?.source === 'categorie' && (
                            <ReportDrillDown
                                title={`Détail des écritures — ${drill.mois}`}
                                endpoint="/api/emis/marge-brute/drill-down"
                                params={drillParams}
                                enabled={true}
                                onClose={() => setDrill(null)}
                                columns={DRILL_COLUMNS}
                            />
                        )}
                    </>
                )}
            </ReportSection>
        </div>
    );
}

function UnavailablePlaceholder() {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <span className="text-3xl font-light">{DASH}</span>
            <p className="mt-2 text-sm">Indicateur indisponible avec ce filtre.</p>
        </div>
    );
}
